import { DateTime } from "luxon";

const DEFAULT_TZ = "America/Los_Angeles";

const HASH_UNIT_DENOMINATOR = 9007199254740992;

// Assignment hashing needs strong avalanche behavior so concurrent experiments
// with similar keys/salts do not become correlated for the same subject id.
const hashStringToUint53 = (value = "", seed = 0) => {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  const input = String(value || "");

  for (let index = 0; index < input.length; index += 1) {
    const charCode = input.charCodeAt(index);
    h1 = Math.imul(h1 ^ charCode, 2654435761);
    h2 = Math.imul(h2 ^ charCode, 1597334677);
  }

  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

const hashStringToUnitInterval = (value = "") =>
  hashStringToUint53(value) / HASH_UNIT_DENOMINATOR;

const stableAssignmentVersion = (
  experiment = {},
  assignment = {},
  unit = "",
  cadence = "",
) => {
  const explicit = String(
    assignment?.assignmentVersion ||
      assignment?.version ||
      experiment?.assignmentVersion ||
      "",
  ).trim();
  if (explicit) return explicit;
  const variants = (
    Array.isArray(assignment?.variants) && assignment.variants.length > 0
      ? assignment.variants
      : experiment?.variants || []
  ).map((variant) => ({
    key: variant?.key || "",
    weight: Math.max(0, Number(variant?.weight ?? 0)),
  }));
  const basis = JSON.stringify({
    experimentKey: experiment?.key || "",
    seedSalt: assignment?.seedSalt || "default",
    unit,
    cadence,
    variants,
  });
  return `registry_assignment_v2_${hashStringToUint53(basis).toString(36)}`;
};

export const getCadenceBucketKey = (
  cadence = "static",
  date = DateTime.now(),
  timeZone = DEFAULT_TZ,
) => {
  const dt = DateTime.isDateTime(date)
    ? date.setZone(timeZone)
    : DateTime.fromJSDate(new Date(date), { zone: timeZone });

  if (cadence === "daily") return dt.toISODate();
  if (cadence === "weekly") return dt.startOf("week").toISODate();
  if (cadence === "monthly") return dt.startOf("month").toFormat("yyyy-LL");
  return "static";
};

export const pickWeightedVariant = (variants = [], roll = 0) => {
  const normalizedVariants = (Array.isArray(variants) ? variants : [])
    .map((variant) => ({
      ...variant,
      weight: Math.max(0, Number(variant?.weight ?? 0)),
    }))
    .filter((variant) => variant?.key && variant.weight > 0);

  if (normalizedVariants.length === 0) {
    return null;
  }

  const totalWeight = normalizedVariants.reduce(
    (sum, variant) => sum + variant.weight,
    0,
  );
  const target =
    Math.min(Math.max(Number(roll) || 0, 0), 0.999999999) * totalWeight;

  let cursor = 0;
  for (const variant of normalizedVariants) {
    cursor += variant.weight;
    if (target < cursor) {
      return variant;
    }
  }

  return normalizedVariants[normalizedVariants.length - 1];
};

const assignmentVariantKeys = (experiment = {}) => {
  const configuredVariants = Array.isArray(experiment?.assignment?.variants)
    ? experiment.assignment.variants
    : [];
  const variants = (
    configuredVariants.length
      ? configuredVariants
      : Array.isArray(experiment?.variants)
        ? experiment.variants
        : []
  ).filter((variant) => Number(variant?.weight ?? 0) > 0);

  return new Set(
    variants
      .map((variant) => String(variant?.key || "").trim())
      .filter(Boolean),
  );
};

const timestampMillis = (value) => {
  if (DateTime.isDateTime(value)) return value.toMillis();
  const millis = new Date(value).getTime();
  return Number.isFinite(millis) ? millis : null;
};

export const isExperimentAssignableAt = (
  experiment = null,
  date = DateTime.now(),
) => {
  if (!experiment?.key || !experiment?.assignment) return false;
  if (experiment.status && experiment.status !== "active") return false;

  const valueMetrics = experiment.valueMetrics || {};
  const atMillis = timestampMillis(date);
  const effectiveStartAt =
    experiment.effectiveStartAt || valueMetrics.effectiveStartAt;
  const effectiveEndAt =
    experiment.effectiveEndAt || valueMetrics.effectiveEndAt;
  if (!effectiveStartAt && !effectiveEndAt) return true;
  const startMillis = timestampMillis(effectiveStartAt);
  const endMillis = effectiveEndAt ? timestampMillis(effectiveEndAt) : null;
  return (
    atMillis !== null &&
    startMillis !== null &&
    atMillis >= startMillis &&
    (endMillis === null || atMillis < endMillis)
  );
};

export const resolveStickyExperimentAssignment = (
  experiment = null,
  assignment = null,
  storedContext = null,
  { eligibleVariantKeys = null } = {},
) => {
  if (!experiment?.key || !assignment?.variantKey || !storedContext) {
    return assignment;
  }

  const metadata =
    storedContext?.metadata && typeof storedContext.metadata === "object"
      ? storedContext.metadata
      : {};
  const storedVariantKey = String(storedContext?.variantKey || "").trim();
  const storedSubjectId = String(storedContext?.subjectId || "").trim();
  const storedSubjectScope = String(storedContext?.subjectScope || "")
    .trim()
    .toLowerCase();
  const storedCadence = String(metadata?.cadence || "static")
    .trim()
    .toLowerCase();
  const storedBucketKey = String(metadata?.bucketKey || "static").trim();
  const storedRevisionId = String(metadata?.revisionId || "").trim();
  const currentRevisionId = String(
    assignment?.revisionId || experiment?.revisionId || "",
  ).trim();
  const eligibleVariants = Array.isArray(eligibleVariantKeys)
    ? new Set(
        eligibleVariantKeys
          .map((variantKey) => String(variantKey || "").trim())
          .filter(Boolean),
      )
    : null;

  if (
    String(storedContext?.experimentKey || "").trim() !== experiment.key ||
    String(storedContext?.source || "")
      .trim()
      .toLowerCase() !== "assignment" ||
    storedSubjectId !== assignment.subjectId ||
    storedSubjectScope !== assignment.unit ||
    storedCadence !== assignment.cadence ||
    storedBucketKey !== assignment.bucketKey ||
    !storedRevisionId ||
    storedRevisionId !== currentRevisionId ||
    !assignmentVariantKeys(experiment).has(storedVariantKey) ||
    (eligibleVariants && !eligibleVariants.has(storedVariantKey))
  ) {
    return assignment;
  }

  return {
    ...assignment,
    variantKey: storedVariantKey,
    assignmentVersion:
      String(metadata?.assignmentVersion || "").trim() ||
      assignment.assignmentVersion,
    restoredFromContext: true,
  };
};

const getAssignmentSubjectId = ({
  unit = "session",
  subjectId = "",
  memberId = "",
  userId = "",
  sessionId = "",
} = {}) => {
  if (subjectId) return String(subjectId);
  if (unit === "member") return memberId ? String(memberId) : "";
  if (unit === "user") return userId ? String(userId) : "";
  return sessionId ? String(sessionId) : "";
};

export const resolveExperimentAssignment = (
  experiment = null,
  {
    subjectId = "",
    memberId = "",
    userId = "",
    sessionId = "",
    date = DateTime.now(),
    timeZone = DEFAULT_TZ,
    eligibleVariantKeys = null,
  } = {},
) => {
  const assignment = experiment?.assignment || null;
  if (
    !experiment?.key ||
    !assignment ||
    !isExperimentAssignableAt(experiment, date)
  ) {
    return null;
  }

  const unit = String(assignment?.unit || "session")
    .trim()
    .toLowerCase();
  const cadence = String(assignment?.cadence || "static")
    .trim()
    .toLowerCase();
  const bucketKey = getCadenceBucketKey(cadence, date, timeZone);
  const resolvedSubjectId = getAssignmentSubjectId({
    unit,
    subjectId,
    memberId,
    userId,
    sessionId,
  });
  if (!resolvedSubjectId || resolvedSubjectId === "anonymous") {
    return null;
  }
  const configuredVariants =
    Array.isArray(assignment?.variants) && assignment.variants.length > 0
      ? assignment.variants
      : experiment?.variants || [];
  const eligibleVariants = Array.isArray(eligibleVariantKeys)
    ? new Set(
        eligibleVariantKeys
          .map((variantKey) => String(variantKey || "").trim())
          .filter(Boolean),
      )
    : null;
  const weightedVariants = eligibleVariants
    ? configuredVariants.filter((variant) =>
        eligibleVariants.has(String(variant?.key || "").trim()),
      )
    : configuredVariants;
  const seed = [
    experiment.key,
    assignment?.seedSalt || "default",
    unit,
    resolvedSubjectId,
    bucketKey,
  ].join("|");
  const roll = hashStringToUnitInterval(seed);
  const chosenVariant = pickWeightedVariant(weightedVariants, roll);

  if (!chosenVariant?.key) {
    return null;
  }

  return {
    experimentKey: experiment.key,
    revisionId: String(
      assignment?.revisionId ||
        experiment?.revisionId ||
        `${experiment.key}_r1`,
    ).trim(),
    variantKey: chosenVariant.key,
    unit,
    cadence,
    bucketKey,
    assignmentVersion: stableAssignmentVersion(
      experiment,
      assignment,
      unit,
      cadence,
    ),
    subjectId: resolvedSubjectId,
    roll,
    ...(Array.isArray(eligibleVariantKeys)
      ? {
          eligibleVariantKeys: weightedVariants.map((variant) => variant.key),
        }
      : {}),
  };
};
